import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Use relative URL in production if REACT_APP_BACKEND_URL is not set
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// Setup axios interceptor for handling 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token on 401 - user will be redirected to login
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Plan feature definitions for feature gating
export const PLAN_FEATURES = {
  free_trial: {
    // Basic features - very limited
    cloaking_enabled: false,
    vla_enabled: false,
    filtering_level_selection: false,
    red_screen_protection: false,
    brand_protection: false,
    spy_protection: false,
    payment_gateway_cloaking: false,
    inapp_webview_pwa_cloaking: false,
    combines_12_cloakers: false,
    guest_access: false,
    api_access: false,
    // Anti-fraud features (basic)
    click_fraud_protection: true,
    browser_fingerprinting: true,
    detailed_reporting: true,
    geo_ip_targeting: true,
    device_filtering: true,
    // Advanced features
    js_fingerprinting: false,
    behavioral_analysis: false,
    ab_testing: false,
    advanced_webhooks: false,
    time_based_filtering: false,
    landing_rotation: false,
    conversion_tracking: false,
    session_recordings: false,
    // Limits
    campaign_limit: 2,
    data_retention_months: 1
  },
  anti_fraud: {
    // $149 Plan - Anti-fraud only, NO CLOAKING
    cloaking_enabled: false,
    vla_enabled: false,
    filtering_level_selection: false,
    red_screen_protection: false,
    brand_protection: false,
    spy_protection: false,
    payment_gateway_cloaking: false,
    inapp_webview_pwa_cloaking: false,
    combines_12_cloakers: false,
    guest_access: false,
    api_access: false,
    // Anti-fraud features
    click_fraud_protection: true,
    browser_fingerprinting: true,
    detailed_reporting: true,
    geo_ip_targeting: true,
    device_filtering: true,
    // Advanced features - NOT included
    js_fingerprinting: false,
    behavioral_analysis: false,
    ab_testing: false,
    advanced_webhooks: false,
    time_based_filtering: false,
    landing_rotation: false,
    conversion_tracking: false,
    session_recordings: false,
    // Limits
    campaign_limit: 20,
    data_retention_months: 6
  },
  personal: {
    // $349 Plan - Cloaking enabled, but limited advanced features
    cloaking_enabled: true,
    vla_enabled: true,
    filtering_level_selection: true,
    red_screen_protection: false,  // Professional only
    brand_protection: false,       // Professional only
    spy_protection: false,         // Professional only
    payment_gateway_cloaking: false, // Professional only
    inapp_webview_pwa_cloaking: false, // Professional only
    combines_12_cloakers: false,   // Professional only
    guest_access: false,           // Professional only
    api_access: false,             // Professional only
    // Anti-fraud features
    click_fraud_protection: true,
    browser_fingerprinting: true,
    detailed_reporting: true,
    geo_ip_targeting: true,
    device_filtering: true,
    // Advanced features - included in Personal
    js_fingerprinting: true,
    behavioral_analysis: true,
    ab_testing: true,
    advanced_webhooks: true,
    time_based_filtering: true,
    landing_rotation: true,
    conversion_tracking: true,
    session_recordings: true,
    // Limits
    campaign_limit: 50,
    data_retention_months: 12
  },
  professional: {
    // $599 Plan - Everything unlimited
    cloaking_enabled: true,
    vla_enabled: true,
    filtering_level_selection: true,
    red_screen_protection: true,
    brand_protection: true,
    spy_protection: true,
    payment_gateway_cloaking: true,
    inapp_webview_pwa_cloaking: true,
    combines_12_cloakers: true,
    guest_access: true,
    api_access: true,
    // Anti-fraud features
    click_fraud_protection: true,
    browser_fingerprinting: true,
    detailed_reporting: true,
    geo_ip_targeting: true,
    device_filtering: true,
    // Advanced features
    js_fingerprinting: true,
    behavioral_analysis: true,
    ab_testing: true,
    advanced_webhooks: true,
    time_based_filtering: true,
    landing_rotation: true,
    conversion_tracking: true,
    session_recordings: true,
    // Limits
    campaign_limit: -1, // unlimited
    data_retention_months: 24
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(PLAN_FEATURES.free_trial);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // Check if user has active paid subscription (not trial, not expired, not cancelled)
  const hasActiveSubscription = () => {
    if (!subscription) return false;
    // Check if subscription status is "active" and it's a paid plan (not free_trial)
    return subscription.status === 'active' && subscription.plan_id !== 'free_trial';
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Also fetch subscription
      await fetchSubscription();
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/current`);
      setSubscription(response.data);
      // Set plan features based on subscription
      const planId = response.data?.plan_id || 'free_trial';
      setPlanFeatures(PLAN_FEATURES[planId] || PLAN_FEATURES.free_trial);
    } catch (error) {
      // If no subscription found, use free trial features
      setSubscription(null);
      setPlanFeatures(PLAN_FEATURES.free_trial);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Fetch subscription after login
    await fetchSubscription();
    
    return userData;
  };

  const register = async (email, password, full_name) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      full_name, 
      role: 'user' 
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Fetch subscription after register
    await fetchSubscription();
    
    return userData;
  };

  const logout = useCallback(async () => {
    // Call backend to clear session (allows login from new device)
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore errors - just clear local state anyway
      console.log('Logout API call failed, clearing local state');
    }
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setSubscription(null);
    setPlanFeatures(PLAN_FEATURES.free_trial);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const refreshSubscription = async () => {
    await fetchSubscription();
  };

  // Check if a specific feature is available in the current plan
  const hasFeature = (featureName) => {
    return planFeatures[featureName] === true;
  };

  // Get the campaign limit for current plan
  const getCampaignLimit = () => {
    return planFeatures.campaign_limit || 2;
  };

  // Check if user can create more campaigns
  const canCreateCampaign = (currentCampaignCount) => {
    const limit = getCampaignLimit();
    if (limit === -1) return true; // unlimited
    return currentCampaignCount < limit;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      subscription, 
      planFeatures,
      login, 
      register, 
      logout, 
      loading,
      refreshSubscription,
      hasActiveSubscription,
      hasFeature,
      getCampaignLimit,
      canCreateCampaign
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
